import { useState, useCallback, useEffect } from 'react';

export type ColumnKey = 'name' | 'price' | 'acquisition_date' | 'status' | 'age' | 'quantity' | 'vendor' | 'tank_bred' | 'thumbnail' | 'category' | 'subcategory';

const VALID_COLUMNS: Set<ColumnKey> = new Set([
  'name', 'price', 'acquisition_date', 'status', 'age', 'quantity', 'vendor', 'tank_bred', 'thumbnail', 'category', 'subcategory'
]);

export interface UseColumnPreferencesResult {
  columns: ColumnKey[];
  toggleColumn: (key: ColumnKey) => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  resetToDefaults: () => void;
  isVisible: (key: ColumnKey) => boolean;
}

function loadFromStorage(key: string, defaults: ColumnKey[]): ColumnKey[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return defaults;

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return defaults;

    // Filter out invalid column keys
    const valid = parsed.filter((col: unknown): col is ColumnKey =>
      typeof col === 'string' && VALID_COLUMNS.has(col as ColumnKey)
    );

    return valid.length > 0 ? valid : defaults;
  } catch {
    return defaults;
  }
}

export function useColumnPreferences(
  storageKey: string,
  defaultColumns: ColumnKey[]
): UseColumnPreferencesResult {
  const [columns, setColumns] = useState<ColumnKey[]>(() =>
    loadFromStorage(storageKey, defaultColumns)
  );

  // Persist to localStorage whenever columns change
  useEffect(() => {
    // Only save if different from defaults (to allow reset to work)
    const isDefault = JSON.stringify(columns) === JSON.stringify(defaultColumns);
    if (isDefault) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    }
  }, [columns, storageKey, defaultColumns]);

  const toggleColumn = useCallback((key: ColumnKey) => {
    // Don't allow hiding the name column
    if (key === 'name') return;

    setColumns(prev => {
      if (prev.includes(key)) {
        return prev.filter(col => col !== key);
      } else {
        // Add at the end
        return [...prev, key];
      }
    });
  }, []);

  const reorderColumns = useCallback((fromIndex: number, toIndex: number) => {
    setColumns(prev => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      if (removed !== undefined) {
        result.splice(toIndex, 0, removed);
      }
      return result;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setColumns(defaultColumns);
    localStorage.removeItem(storageKey);
  }, [defaultColumns, storageKey]);

  const isVisible = useCallback((key: ColumnKey) => {
    return columns.includes(key);
  }, [columns]);

  return {
    columns,
    toggleColumn,
    reorderColumns,
    resetToDefaults,
    isVisible,
  };
}
